import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import Dropzone from "@/features/upload/components/DropZone";
import { Card } from "../../lib/components/ui/card";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../../lib/components/ui/alert";
import { Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../lib/components/ui/select";
import ModelViewer from "@/features/upload/components/ModelViewer";
// --- START EDIT: import long-timeout axios instance for heavy operations ---
import axiosInstance, { axiosLong } from "@/auth/axiosWithInterceptor";
// --- END EDIT: import long-timeout axios instance for heavy operations ---
import { ErrorBoundary } from "@/components/ErrorBoundary";
import FilamentPicker from "@/features/upload/components/FilamentPicker";
import { Button } from "@/lib/components/ui/button";
import { useAuth } from "@/auth/useAuth";
import { useCart } from "@/features/cart/hooks/useCart";
import AddToCartDialog from "@/features/cart/components/AddToCartDialog";
import FurtherInspectionDialog from "@/features/inventory/modals/FurtherInspectionDialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

// --- START EDIT: Helper to build absolute backend URLs for model viewer ---
const getBackendBaseUrl = (): string => {
  const env: ImportMetaEnv | undefined = 
    typeof import.meta !== 'undefined' ? import.meta.env : undefined;
  const apiBaseFromEnv = env?.VITE_API_BASE_URL?.trim();
  const apiBaseFromWindow = 
    typeof window !== 'undefined' ? window.location.origin : undefined;
  return apiBaseFromEnv || apiBaseFromWindow || 'http://localhost:8000';
};
// --- END EDIT: Helper to build absolute backend URLs for model viewer ---

interface SliceEstimate {
  filament_used_m: number;
  filament_used_g?: number | null;
  estimated_price: number;
  support_enabled: boolean;
  params_hash: string;
}

interface UploadResponse {
  model_name: string;
  oriented_model_url?: string;
  warning?: string;
  fit_type?: string;
  aabb?: number[];
  obb?: number[];
  needs_supports?: boolean;
  status?: string;
  scale_factor?: number;
  support_required?: boolean;
  too_large?: boolean;
  max_dim?: number;
  suggested_scale?: number;
  repair_warning?: string;
  // New fields for original file storage
  original_file_key?: string;
  original_file_url?: string;
  file_id?: string;
}

interface ScaleResponse {
  scaled_model_url: string;
}

const UploadModel = () => {
  const { user } = useAuth();
  const { increment, refresh: refreshCart } = useCart();
  const { t } = useTranslation('upload');

  // Filament selection
  const [selectedFilamentId, setSelectedFilamentId] = useState<number | null>(null);

  // Model processing state
  const [uploadResult, setUploadResult] = useState<UploadResponse | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // User-configurable slicing parameters
  const [layerHeight, setLayerHeight] = useState("0.2");
  const [infillPercent, setInfillPercent] = useState("20");
  const [infillGeometry, setInfillGeometry] = useState("grid");

  // Files / orientation / scaling
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [scaledUrl, setScaledUrl] = useState<string | null>(null);
  const [scaledDims, setScaledDims] = useState<number[] | null>(null);

  // Slicing estimate
  const [sliceEstimate, setSliceEstimate] = useState<SliceEstimate | null>(null);
  const [isSlicing, setIsSlicing] = useState(false);

  // Dialog state
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openFurtherDialog, setOpenFurtherDialog] = useState(false);
  const [furtherIsTooLarge, setFurtherIsTooLarge] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [scalingBusy, setScalingBusy] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  // --- START EDIT: ModelFile caching for parameter reuse in session ---
  // Cache maps merge key -> model_file_id to reuse ModelFile when parameters unchanged
  const [modelFileCache, setModelFileCache] = useState<Map<string, number>>(new Map());
  
  // Helper to compute cache key matching backend merge logic
  const computeCacheKey = (
    filename: string,
    filamentTypeId: number | null,
    layerThickness: string,
    infillPct: string,
    infillGeo: string,
    paramsHash: string
  ): string => {
    const normalizedFilename = filename.trim().toLowerCase();
    const normalizedGeometry = infillGeo.trim().toLowerCase();
    // Convert to numeric types to match backend merge key exactly
    const layerThicknessNum = parseFloat(layerThickness);
    const infillPctNum = parseInt(infillPct, 10);
    // Use JSON.stringify for robust serialization to avoid key collisions
    return JSON.stringify({
      filename: normalizedFilename,
      filamentTypeId,
      layerThickness: layerThicknessNum,
      infillPct: infillPctNum,
      infillGeo: normalizedGeometry,
      paramsHash
    });
  };
  // --- END EDIT: ModelFile caching ---

  // --- START EDIT: Derive viewer URL (absolute backend) and raw path for API calls ---
  // Viewer URL: absolute backend URL for ModelViewer to load STL/OBJ from backend domain
  const viewerModelUrl = useMemo(() => {
    const backendBase = getBackendBaseUrl();
    if (scaledUrl) {
      // scaledUrl is a raw path like "/model/scaled-file/..."
      return `${backendBase}/api${scaledUrl}`;
    }
    if (uploadResult?.oriented_model_url) {
      // oriented_model_url is a raw path like "/model/oriented-file/..."
      return `${backendBase}/api${uploadResult.oriented_model_url}`;
    }
    return null;
  }, [scaledUrl, uploadResult?.oriented_model_url]);

  // Raw path for API calls (scale, slice-estimate): no /api prefix, no absolute URL
  const rawModelPath = useMemo(() => {
    if (scaledUrl) return scaledUrl;
    if (uploadResult?.oriented_model_url) return uploadResult.oriented_model_url;
    return null;
  }, [scaledUrl, uploadResult?.oriented_model_url]);
  // --- END EDIT: Derive viewer URL (absolute backend) and raw path for API calls ---

  const runSliceEstimate = useCallback(async () => {
    // --- START EDIT: Use raw path for slice-estimate API call ---
    if (!rawModelPath || !selectedFilamentId) return;
    // --- END EDIT: Use raw path for slice-estimate API call ---

    setIsSlicing(true);
    setError(null);
    setSliceEstimate(null);

    try {
      // --- START EDIT: use long-timeout axios for slicing estimate ---
      const res = await axiosLong.post<SliceEstimate>(
        "/model/slice-estimate/",
        null,
        {
          params: {
            // --- START EDIT: Pass raw path (no /api prefix) to slice-estimate ---
            file_url: rawModelPath,
            // --- END EDIT: Pass raw path (no /api prefix) to slice-estimate ---
            layer_height: layerHeight,
            infill_pct: infillPercent,
            infill_geometry: infillGeometry,
            generate_supports: false,
            filament_inventory_id: selectedFilamentId,
          },
        }
      );
      // --- END EDIT: use long-timeout axios for slicing estimate ---
      setSliceEstimate(res.data);
    } catch (err: unknown) {
      setOpenFurtherDialog(true);
      setFurtherIsTooLarge(false);
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || "Slicing failed.");
      } else {
        setError("Slicing failed. Please try again.");
      }
      setSliceEstimate(null);
    } finally {
      setIsSlicing(false);
    }
  }, [
    // --- START EDIT: Update dependency to use rawModelPath ---
    rawModelPath,
    // --- END EDIT: Update dependency to use rawModelPath ---
    selectedFilamentId,
    layerHeight,
    infillPercent,
    infillGeometry,
  ]);

  useEffect(() => {
    runSliceEstimate();
  }, [runSliceEstimate]);

  const handleUpload = async (file: File) => {
    setUploadedFile(file);
    setIsUploading(true);
    setError(null);
    setUploadResult(null);
    setScaledUrl(null);
    setScaledDims(null);
    // Clear cache on new file upload
    setModelFileCache(new Map());

    // Check if direct upload to R2 is enabled
    const useDirectUpload = import.meta.env.VITE_USE_DIRECT_UPLOAD === 'true';

    try {
      if (useDirectUpload) {
        // New flow: Direct upload to R2 via presigned URL
        
        // --- START EDIT: Ensure Content-Type used for presign matches the PUT; sanitize presigned URL before PUT ---
        // Compute a reliable content type for STL/OBJ (File.type is often empty)
        const ext = (file.name?.split(".").pop() || "").toLowerCase();
        const computedContentType =
          file.type && file.type.trim().length > 0
            ? file.type
            : ext === "stl"
            ? "model/stl"
            : ext === "obj"
            ? "model/obj"
            : "application/octet-stream";

        // Step 1: Request presigned upload URL (send the same content_type we will PUT with)
        const presignRes = await axiosInstance.post<{
          upload_url: string;
          storage_key: string;
          expires_in: number;
          // Authoritative content_type from backend (we will respect this on PUT)
          content_type: string;
        }>('storage/presign-upload', {
          filename: file.name,
          content_type: computedContentType,
        });

        const { upload_url, storage_key, content_type } = presignRes.data;

        // Use the backend-returned content_type if provided
        const putContentType = content_type || computedContentType;

        // Sanitize any stray CR/LF characters that could break AWS SigV4 credential parsing
        const cleanUploadUrl = upload_url.replace(/\r?\n/g, "");
        // --- END EDIT: Ensure Content-Type used for presign matches the PUT; sanitize presigned URL before PUT ---

        // Step 2: Upload file directly to R2 using presigned URL
        await axios.put(cleanUploadUrl, file, {
          headers: {
            // --- START EDIT: PUT with the exact content type from presign ---
            'Content-Type': putContentType,
            // --- END EDIT: PUT with the exact content type from presign ---
          },
          // Large files: let Axios stream without body length limits
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
        });

        // Step 3: Call backend to process the uploaded file
        // --- START EDIT: use long-timeout axios for model processing ---
        const processRes = await axiosLong.post<UploadResponse>(
          'model/upload-orient',
          null,
          {
            params: {
              storage_key: storage_key,
              original_filename: file.name,
            },
          }
        );
        // --- END EDIT: use long-timeout axios for model processing ---

        setUploadResult(processRes.data);

        if (processRes.data.status === 'too_large' || processRes.data.too_large) {
          setFurtherIsTooLarge(true);
          setOpenAddDialog(false);
          setOpenFurtherDialog(true);
        }
      } else {
        // Legacy flow: Multipart upload to backend
        const formData = new FormData();
        formData.append('file', file);

        // --- START EDIT: use long-timeout axios for legacy upload as well ---
        const res = await axiosLong.post<UploadResponse>(
          'model/upload-orient',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        // --- END EDIT: use long-timeout axios for legacy upload as well ---
        setUploadResult(res.data);

        if (res.data.status === 'too_large' || res.data.too_large) {
          setFurtherIsTooLarge(true);
          setOpenAddDialog(false);
          setOpenFurtherDialog(true);
        }
      }
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || 'Upload failed.');
      } else {
        setError('Upload failed. Please try again.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleScale = async () => {
    if (!uploadResult?.oriented_model_url || !uploadResult?.suggested_scale)
      return;
    setScalingBusy(true);
    setError(null);

    try {
      // --- START EDIT: Pass raw oriented path (no /api prefix) to scale endpoint and use long-timeout axios ---
      const res = await axiosLong.post<ScaleResponse>("model/scale", null, {
        params: {
          file_url: uploadResult.oriented_model_url,
          scale_factor: uploadResult.suggested_scale,
        },
      });
      // --- END EDIT: Pass raw oriented path (no /api prefix) to scale endpoint and use long-timeout axios ---

      const newDims =
        uploadResult.obb?.map((d) => d * (uploadResult.suggested_scale ?? 1)) ?? null;

      // --- START EDIT: Store raw scaled path (no /api prefix, no absolute URL) ---
      setScaledUrl(res.data.scaled_model_url);
      // --- END EDIT: Store raw scaled path (no /api prefix, no absolute URL) ---
      setScaledDims(newDims);
      setOpenFurtherDialog(false);
      toast.success(t('success.scaled'));
      setTimeout(() => runSliceEstimate(), 0);
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.detail || t('errors.processingFailed'));
      } else {
        setError(t('errors.processingFailed'));
      }
      toast.error(t('errors.processingFailed'));
    } finally {
      setScalingBusy(false);
    }
  };

  // --- START EDIT: Render viewer with absolute backend URL ---
  const renderModelViewer = () => {
    if (viewerModelUrl) {
      // Viewer URL is absolute backend URL (e.g., https://api.dm-3d-printing.com/api/model/oriented-file/...)
      return <ModelViewer url={viewerModelUrl} />;
    }
    if (uploadedFile) return <ModelViewer file={uploadedFile} />;
    return <ModelViewer />;
  };
  // --- END EDIT: Render viewer with absolute backend URL ---

  const canOpenAddToCart =
    // --- START EDIT: Use rawModelPath for validation ---
    !!rawModelPath && !!selectedFilamentId && !!layerHeight && !!infillPercent && !!sliceEstimate?.params_hash;
    // --- END EDIT: Use rawModelPath for validation ---

  const handleAddToCart = async (qty: number) => {
    if (!canOpenAddToCart || !sliceEstimate || !selectedFilamentId || !uploadResult?.model_name) return;

    setAddingToCart(true);
    try {
      // --- START EDIT: ModelFile caching logic ---
      // Compute cache key based on merge parameters
      const cacheKey = computeCacheKey(
        uploadResult.model_name,
        selectedFilamentId,
        layerHeight,
        infillPercent,
        infillGeometry,
        sliceEstimate.params_hash
      );

      // Check if we have a cached ModelFile for these exact parameters
      let modelFileId = modelFileCache.get(cacheKey);

      if (!modelFileId) {
        // Cache miss: create new ModelFile and cache it
        // --- START EDIT: Pass raw path (no /api prefix) for file_url ---
        const createRes = await axiosInstance.post("/models", {
          file_url: rawModelPath,
          original_file_key: uploadResult?.original_file_key,
          original_filename: uploadResult.model_name,
          file_id: uploadResult?.file_id,
          params_hash: sliceEstimate.params_hash,
          layer_thickness: parseFloat(layerHeight),
          infill_percentage: parseInt(infillPercent, 10),
          infill_geometry: infillGeometry,
          supports_detected: Boolean(uploadResult?.support_required),
          filament_inventory_id: selectedFilamentId,
        });
        // --- END EDIT: Pass raw path (no /api prefix) for file_url ---

        modelFileId = createRes?.data?.id;
        if (!modelFileId) {
          throw new Error("Model file creation returned no id.");
        }

        // Cache the created ModelFile ID for this parameter combination
        setModelFileCache((prev) => new Map(prev).set(cacheKey, modelFileId!));
      }
      // Cache hit: reuse existing ModelFile (modelFileId is already set)
      // --- END EDIT: ModelFile caching logic ---

      // IMPORTANT: send the exact selected roll so cart snapshots the correct unit price
      await axiosInstance.post("/cart/items", {
        model_file_id: modelFileId,
        quantity: qty,
        filament_inventory_id: selectedFilamentId,
      });

      // --- START: Optimistic badge update + server reconciliation ---
      increment(qty);
      refreshCart();
      // --- END: Optimistic badge update + server reconciliation ---

      setOpenAddDialog(false);
      toast.success(t('success.addedToCart', { 
        qty, 
        price: (sliceEstimate.estimated_price ?? 0).toFixed(2) 
      }));
    } catch (e) {
      console.error(e);
      setOpenAddDialog(false);
      toast.error(t('errors.addToCartFailed'));
    } finally {
      setAddingToCart(false);
    }
  };

  const handleManualReview = async (email: string, message: string) => {
    setSubmittingReview(true);
    try {
      // --- START EDIT: Pass raw path (no /api prefix) for file_url ---
      await axiosInstance.post("/models/manual-review", {
        file_url: rawModelPath,
        email: email,
        message,
        original_file_key: uploadResult?.original_file_key || null,
      });
      // --- END EDIT: Pass raw path (no /api prefix) for file_url ---
      setOpenFurtherDialog(false);
      toast.success(t('success.manualReviewRequested'));
    } catch (e) {
      console.error(e);
      toast.error(t('errors.processingFailed'));
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div className="bg-muted min-h-[calc(100vh-64px)] transition-colors">
      {/* Hero section (matches other pages) */}
      <section className="px-6 pt-12 pb-6">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">
            {t('hero.title')}
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Content section */}
      <section className="px-6 pb-16">
        <div className="flex flex-col md:flex-row gap-6 justify-center items-start max-w-6xl mx-auto">
          <div className="flex-shrink-0 w-full md:w-[540px]">
            <Card className="p-4 min-h-[480px] flex flex-col justify-center rounded-xl shadow-sm">
              <ErrorBoundary>{renderModelViewer()}</ErrorBoundary>
            </Card>
          </div>

          <div className="w-full md:w-[370px] flex flex-col gap-4">
            <Card className="p-6 rounded-xl shadow-sm">
              <Dropzone onFileAccepted={handleUpload} />
            </Card>

            <Card className="p-6 rounded-xl shadow-sm">
              <h2 className="text-sm font-semibold tracking-wide text-muted-foreground uppercase mb-2">
                {t('printSettings')}
              </h2>

              {/* Group rhythm 16px; each field uses a grid with 10px gap between label and input */}
              <div className="space-y-4">
                <FilamentPicker
                  selectedFilamentId={selectedFilamentId}
                  setSelectedFilamentId={(id) => setSelectedFilamentId(id)}
                />

                {/* --- START EDIT: Layer Height label/input spacing --- */}
                <div className="grid gap-2.5">
                  <label className="block text-sm font-medium text-foreground">
                    Layer Height
                  </label>
                  <Select value={layerHeight} onValueChange={setLayerHeight}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectLayerHeight')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.1">0.1mm</SelectItem>
                      <SelectItem value="0.2">0.2mm</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* --- END EDIT: Layer Height label/input spacing --- */}

                {/* --- START EDIT: Infill % label/input spacing --- */}
                <div className="grid gap-2.5">
                  <label className="block text-sm font-medium text-foreground">
                    Infill %
                  </label>
                  <Select value={infillPercent} onValueChange={setInfillPercent}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectInfillPercent')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="20">20%</SelectItem>
                      <SelectItem value="50">50%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* --- END EDIT: Infill % label/input spacing --- */}

                {/* --- START EDIT: Infill Geometry label/input spacing --- */}
                <div className="grid gap-2.5">
                  <label className="block text-sm font-medium text-foreground">
                    Infill Geometry
                  </label>
                  <Select value={infillGeometry} onValueChange={setInfillGeometry}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectInfillType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grid">Grid</SelectItem>
                      <SelectItem value="gyroid">Gyroid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* --- END EDIT: Infill Geometry label/input spacing --- */}

                <Button
                  onClick={() => setOpenAddDialog(true)}
                  disabled={!canOpenAddToCart}
                  className="mt-2"
                >
                  {t('addToCart')}
                </Button>
              </div>
            </Card>

            {isUploading && (
              <div className="flex items-center justify-center mt-4 text-muted-foreground">
                <Loader2 className="animate-spin mr-2 h-5 w-5" />
                {t('uploading')}
              </div>
            )}

            {scaledDims && (
              <Alert className="mt-4">
                <AlertTitle>{t('modelScaled')}</AlertTitle>
                <AlertDescription>
                  {t('newDimensions')}: {scaledDims.map((d) => d.toFixed(2)).join(" × ")} mm
                </AlertDescription>
              </Alert>
            )}

            {uploadResult && (
              <Alert className="mt-4">
                <AlertTitle>{t('modelUploaded')}</AlertTitle>
                <AlertDescription>{t('file')}: {uploadResult.model_name}</AlertDescription>
              </Alert>
            )}

            {uploadResult?.support_required && (
              <Alert variant="destructive" className="mt-4">
                <AlertTitle>{t('supportsMightBeNeeded')}</AlertTitle>
                <AlertDescription>
                  {t('supportsMessage')}
                </AlertDescription>
              </Alert>
            )}

            {isSlicing && (
              <div className="flex items-center justify-center mt-2 text-muted-foreground">
                <Loader2 className="animate-spin mr-1 h-4 w-4" />
                {t('slicing')}
              </div>
            )}

            {sliceEstimate && (
              <Alert className="mt-4">
                <AlertTitle>{t('slicingEstimate')}</AlertTitle>
                <AlertDescription>
                  {t('estimatedPrice')}:{" "}
                  <span className="font-bold text-foreground">
                    €{sliceEstimate.estimated_price.toFixed(2)}
                  </span>
                </AlertDescription>
              </Alert>
            )}

            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertTitle>{t('error')}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        </div>
      </section>

      <AddToCartDialog
        open={openAddDialog}
        onOpenChange={setOpenAddDialog}
        unitPrice={sliceEstimate?.estimated_price ?? null}
        defaultQty={1}
        busy={addingToCart}
        onConfirm={(qty) => handleAddToCart(qty)}
      />

      <FurtherInspectionDialog
        open={openFurtherDialog}
        onOpenChange={setOpenFurtherDialog}
        defaultEmail={user?.email}
        showScaleOption={furtherIsTooLarge}
        suggestedScale={uploadResult?.suggested_scale ?? null}
        originalObb={uploadResult?.obb ?? null}
        submitting={submittingReview}
        scaling={scalingBusy}
        onScaleToFit={furtherIsTooLarge ? handleScale : undefined}
        onSubmitReview={handleManualReview}
      />
    </div>
  );
};

export default UploadModel;